import { useEffect, useMemo, useState } from 'react'
import { Search, Plus, Sparkles } from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { SERVICE_TYPES } from '@/constants/service-types'
import { SERVICE_CATEGORIES } from '@/constants/service-categories'
import { ServiceCard, ServiceDetailModal, ServiceDebugModal, ServiceEditModal, ServiceLogo } from '@/components/services'
import CodeTemplateModal from '@/components/services/CodeTemplateModal'

// API 调用辅助函数
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('admin_token')
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

interface FormData {
  name: string
  description: string
  detailed_description: string
  provider: string
  service_id: string
  api_endpoint: string
  api_key_encrypted: string
  points_cost: number
  max_tokens: number
  timeout_seconds: number
  status: 'active' | 'inactive'
  icon_url: string
  category: string
  features: string[]
  usage_instructions: string
}

export default function ServicesPage() {
  const [services, setServices] = useState<AIService[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingService, setEditingService] = useState<AIService | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [detailService, setDetailService] = useState<AIService | null>(null)
  const [recentLogs, setRecentLogs] = useState<any[]>([])
  const [loadingLogs, setLoadingLogs] = useState(false)

  // Debug modal states
  const [showDebugModal, setShowDebugModal] = useState(false)
  const [debugService, setDebugService] = useState<AIService | null>(null)
  const [testResult, setTestResult] = useState<string>('')
  const [testing, setTesting] = useState(false)
  const [testRequestBody, setTestRequestBody] = useState(JSON.stringify({
    parameters: { input: '你好，这是测试' }
  }, null, 2))

  // Code template modal states
  const [showCodeTemplateModal, setShowCodeTemplateModal] = useState(false)
  const [codeTemplateService, setCodeTemplateService] = useState<AIService | null>(null)

  // Test user states
  const [testUsers, setTestUsers] = useState<Array<{ id: string; email: string; name: string; balance: number }>>([])
  const [selectedTestUser, setSelectedTestUser] = useState<{ id: string; email: string; name: string; balance: number } | null>(null)
  const [testApiKeys, setTestApiKeys] = useState<Array<{ id: string; key_value?: string; key_prefix?: string; name: string }>>([])
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>('')
  const [selectedTestApiKey, setSelectedTestApiKey] = useState<string>('')
  const [loadingTestUser, setLoadingTestUser] = useState(false)

  useEffect(() => {
    void fetchServices()
    void fetchTestUsers()
  }, [])

  const fetchServices = async () => {
    try {
      const result = await apiCall('/api/admin/services')
      setServices(result.data ?? [])
    } catch (error) {
      alert(`获取服务列表失败: ${error instanceof Error ? error.message : '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingService(null)
    setShowModal(true)
  }

  const openEditModal = (service: AIService, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }
    setEditingService(service)
    setShowModal(true)
    setShowDetailModal(false)
  }

  const openDetailModal = (service: AIService) => {
    setDetailService(service)
    setShowDetailModal(true)
    void fetchRecentLogs(service.id)
  }

  const openDebugModal = (service: AIService) => {
    setDebugService(service)
    setShowDebugModal(true)
    setTestResult('')
  }

  const openCodeTemplateModal = (service: AIService) => {
    setCodeTemplateService(service)
    setShowCodeTemplateModal(true)
  }

  const fetchRecentLogs = async (serviceId: string) => {
    setLoadingLogs(true)
    try {
      const result = await apiCall(`/api/admin/logs?service_id=${serviceId}&limit=10`)
      setRecentLogs(result.data ?? [])
    } catch (error) {
      console.error('Failed to fetch logs', error)
    } finally {
      setLoadingLogs(false)
    }
  }

  const fetchTestUsers = async () => {
    setLoadingTestUser(true)
    try {
      // 获取按 balance 倒序的 10 个用户
      const usersResult = await apiCall('/api/admin/users?order_by=balance&order_asc=false&limit=10')
      const users = usersResult.data ?? []

      if (users.length > 0) {
        setTestUsers(users)
        // 默认选择第一个用户
        const firstUser = users[0]
        setSelectedTestUser(firstUser)
        // 获取该用户的 API keys
        await fetchApiKeys(firstUser.id)
      }
    } catch (error) {
      console.error('Failed to fetch test users', error)
    } finally {
      setLoadingTestUser(false)
    }
  }

  const fetchApiKeys = async (userId: string) => {
    try {
      const keysResult = await apiCall(`/api/admin/api-keys?user_id=${userId}&status=active&limit=10`)
      const keys = keysResult.data ?? []

      setTestApiKeys(keys)
      // 默认选择第一个 API key（如果有）
      if (keys.length > 0) {
        setSelectedApiKeyId(keys[0].id)
      } else {
        setSelectedApiKeyId('')
      }
      // 注意：数据库只存储 key_hash，不存储明文 key
      // 用户需要手动输入 API key 进行测试
      setSelectedTestApiKey('sk_test_debug_key_for_development_only') // 默认使用测试 key
    } catch (error) {
      console.error('Failed to fetch API keys', error)
      setTestApiKeys([])
      setSelectedApiKeyId('')
      setSelectedTestApiKey('')
    }
  }

  const handleTestUserChange = async (userId: string) => {
    const user = testUsers.find(u => u.id === userId)
    if (user) {
      setSelectedTestUser(user)
      await fetchApiKeys(user.id)
    }
  }

  const handleTestService = async () => {
    if (!debugService || !selectedTestApiKey) {
      alert('请选择测试用户和 API Key')
      return
    }

    setTesting(true)
    setTestResult('')

    // 直接调用 Worker API
    const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://proxy.apimakt.com'
    const apiUrl = `${workerUrl}/api/v1/proxy/${debugService.id}` // 使用服务的 UUID

    const requestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${selectedTestApiKey}`,
    }

    try {
      const parsedBody = JSON.parse(testRequestBody)
      const startTime = Date.now()

      // 构建请求体（根据 provider 类型）
      let requestBody: any
      switch (debugService.provider) {
        case 'coze-workflow':
          requestBody = {
            parameters: {
              parameters: parsedBody
            },
            operation_type: 'execute'
          }
          break
        case 'coze-agent':
          requestBody = {
            parameters: parsedBody,
            operation_type: 'chat'
          }
          break
        default:
          requestBody = parsedBody
          break
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout((debugService.timeout_seconds + 5) * 1000),
      })

      const endTime = Date.now()
      const totalTime = endTime - startTime

      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })

      const responseText = await response.text()
      let responseData: any
      try {
        responseData = JSON.parse(responseText)
      } catch {
        responseData = responseText
      }

      const debugInfo: Record<string, any> = {
        '测试概览': {
          成功: response.ok,
          HTTP状态码: response.status,
          状态文本: response.statusText,
          总耗时: `${totalTime}ms`,
        },
        '请求信息': {
          接口: apiUrl,
          方法: 'POST',
          服务UUID: debugService.id,
          服务名称: debugService.name,
          服务类型: debugService.provider,
          第三方服务ID: debugService.service_id,
          测试用户: selectedTestUser ? `${selectedTestUser.email || selectedTestUser.name} (余额: ${selectedTestUser.balance})` : 'N/A',
          请求头: requestHeaders,
          请求体: requestBody,
        },
        '响应信息': {
          响应头: responseHeaders,
          响应体: responseData,
        },
      }

      setTestResult(JSON.stringify(debugInfo, null, 2))
    } catch (error) {
      const errorInfo = {
        错误: true,
        错误类型: error instanceof Error ? error.constructor.name : typeof error,
        错误信息: error instanceof Error ? error.message : String(error),
        错误栈: error instanceof Error ? error.stack : undefined,
      }
      setTestResult(JSON.stringify(errorInfo, null, 2))
    } finally {
      setTesting(false)
    }
  }

  const resetForm = () => {
    setShowModal(false)
    setEditingService(null)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      if (editingService) {
        await apiCall(`/api/admin/services/${editingService.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        })
        alert('服务更新成功')
      } else {
        await apiCall('/api/admin/services', {
          method: 'POST',
          body: JSON.stringify(formData),
        })
        alert('服务创建成功')
      }
      await fetchServices()
      resetForm()
    } catch (error) {
      alert(`操作失败: ${error instanceof Error ? error.message : '未知错误'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('确定要删除这个服务吗？')) return

    try {
      await apiCall(`/api/admin/services/${id}`, { method: 'DELETE' })
      alert('删除成功')
      await fetchServices()
    } catch (error) {
      alert(`删除失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = !searchQuery ||
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesType = selectedType === 'all' || service.provider === selectedType
      const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory
      return matchesSearch && matchesType && matchesCategory
    })
  }, [services, searchQuery, selectedType, selectedCategory])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <section className="min-h-screen py-6 px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">服务管理</h1>
            <p className="text-gray-600">管理和配置 AI 服务接口</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            新建服务
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索服务名称或描述..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">服务分类</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              全部 ({services.length})
            </button>
            {SERVICE_CATEGORIES.map((category) => {
              const count = services.filter(s => s.category === category.id).length
              const Icon = category.icon
              const isSelected = selectedCategory === category.id
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 border ${
                    isSelected
                      ? `${category.color.badge} border-2 shadow-lg`
                      : `bg-white ${category.color.text} ${category.color.border} hover:${category.color.bg} hover:shadow-md`
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{category.name}</span>
                  <span className="text-xs opacity-70">({count})</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Type Filter */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">服务类型</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                selectedType === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:shadow-md'
              }`}
            >
              全部类型
            </button>
            {SERVICE_TYPES.map((type) => {
              const count = services.filter(s => s.provider === type.id).length
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedType === type.id
                      ? `${type.bgColor} ${type.color} border-2 border-current shadow-lg`
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <ServiceLogo provider={type.id} size="sm" />
                  <span>{type.displayName}</span>
                  <span className="text-xs opacity-70">({count})</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div>
        {filteredServices.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || selectedCategory !== 'all' || selectedType !== 'all'
                ? '没有找到匹配的服务'
                : '还没有配置任何服务'}
            </h3>
            <p className="text-gray-600 mb-6">创建第一个 AI 服务开始使用</p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5" />
              新建服务
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={openEditModal}
                onDelete={handleDelete}
                onViewDetail={openDetailModal}
                onDebug={openDebugModal}
                onCodeTemplate={openCodeTemplateModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <ServiceEditModal
        service={editingService}
        isOpen={showModal}
        onClose={resetForm}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Detail Modal */}
      <ServiceDetailModal
        service={detailService}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onEdit={openEditModal}
        recentLogs={recentLogs}
        loadingLogs={loadingLogs}
      />

      {/* Debug Modal */}
      <ServiceDebugModal
        service={debugService}
        isOpen={showDebugModal}
        onClose={() => setShowDebugModal(false)}
        testRequestBody={testRequestBody}
        setTestRequestBody={setTestRequestBody}
        testing={testing}
        testResult={testResult}
        onTest={handleTestService}
        testUsers={testUsers}
        selectedTestUser={selectedTestUser}
        testApiKeys={testApiKeys}
        selectedApiKeyId={selectedApiKeyId}
        selectedTestApiKey={selectedTestApiKey}
        loadingTestUser={loadingTestUser}
        onTestUserChange={handleTestUserChange}
        onApiKeySelect={setSelectedApiKeyId}
        onTestApiKeyChange={setSelectedTestApiKey}
      />

      {/* Code Template Modal */}
      <CodeTemplateModal
        isOpen={showCodeTemplateModal}
        onClose={() => setShowCodeTemplateModal(false)}
        service={codeTemplateService}
        apiKey={selectedTestApiKey}
      />
    </section>
  )
}
